import { round2 } from "../contexts/AppContext";
import { formatILS, formatUSD, resolvePublicPackageDisplayPrice } from "./formatCurrency";

/**
 * מחירי חבילות LDS ב־FE מחושבים כ־ILS ב־ourTotalFare.priceWithCommissionAndCreditCardFee
 * (ILS SSOT אחרי המרת מטבע ספק — USD/EUR/GBP/אחר — ושכבות עמלה).
 * להצגת USD: מחלקים בשער USD מהאדמין (כמה ש"ח לדולר אחד).
 * עיגול לדולר שלם הוא לתצוגה בלבד (formatUSD / Math.round) — לא משנה חיוב, שער או ILS.
 */

export function ilsTotalFareToUsdAmount(ilsAmount, usdToIlsRate) {
  const rate = Number(usdToIlsRate);
  const ils = Number(ilsAmount);
  if (!Number.isFinite(rate) || rate <= 0 || !Number.isFinite(ils) || ils < 0) return null;
  return round2(ils / rate);
}

/** דולר ראשי (מעוגל למספר שלם) מתוך סכום ש"ח מערכת + שער USD מהאדמין */
export function formatUsdPrimaryIntegerFromIls(ilsAmount, usdToIlsRate) {
  const usd = ilsTotalFareToUsdAmount(ilsAmount, usdToIlsRate);
  if (usd == null) return null;
  return formatUSD(usd);
}

function formatUsdPerPersonFromIls(ilsAmount, usdToIlsRate, passengerCount) {
  const pax = Math.max(1, Math.floor(Number(passengerCount)) || 0);
  const ils = Number(ilsAmount);
  if (!Number.isFinite(ils) || ils <= 0) return null;
  return formatUsdPrimaryIntegerFromIls(ils / pax, usdToIlsRate);
}

function isCoupleRoomComposition(roomInfo, passengerCount) {
  const rooms = Array.isArray(roomInfo?.rooms) ? roomInfo.rooms : [];
  if (rooms.length === 0) return Math.max(1, Number(passengerCount) || 0) === 2;
  let adults = 0;
  let kids = 0;
  for (const room of rooms) {
    adults += Number(room?.adults ?? room.AdultsCount ?? room.adultsCount ?? 0) || 0;
    kids +=
      Number(room?.kids ?? room.KidsCount ?? room.kidsCount ?? room.children ?? 0) || 0;
  }
  return rooms.length === 1 && adults === 2 && kids === 0;
}

/**
 * תצוגת USD לכרטיסי בית / discovery — לאדם בגדול, סה״כ חבילה בקטן.
 * עמוד תוצאות השוק הציבורי משתמש ב־buildMarketplacePackagePriceDisplay (סה״כ ₪ ראשי).
 * @returns {{
 *   primaryUsdLine: string|null,
 *   perPersonUnit: string|null,
 *   totalUsdLine: string|null,
 *   perPersonHint: string|null,
 * }}
 */
export function buildPublicPackageCardUsdCurrencyDisplay(
  ilsAmount,
  usdToIlsRate,
  passengerCount,
  roomInfo = null
) {
  const pax = Math.max(1, Math.floor(Number(passengerCount)) || 0);
  const primaryUsdLine = formatUsdPerPersonFromIls(ilsAmount, usdToIlsRate, pax);
  if (primaryUsdLine == null) {
    return {
      primaryUsdLine: null,
      perPersonUnit: null,
      totalUsdLine: null,
      perPersonHint: null,
    };
  }
  const usdTotal = formatUsdPrimaryIntegerFromIls(ilsAmount, usdToIlsRate);
  const totalSuffix = isCoupleRoomComposition(roomInfo, pax) ? "לזוג" : "להרכב";
  const totalUsdLine = usdTotal && pax > 1 ? `${usdTotal} ${totalSuffix}` : null;
  return {
    primaryUsdLine,
    perPersonUnit: "לאדם",
    totalUsdLine,
    perPersonHint: totalUsdLine,
  };
}

/**
 * תצוגת מחיר לכרטיס תוצאות שוק — אותם סכומי ILS/USD, היררכיית הצגה בלבד.
 * ראשי: סה״כ החבילה בש״ח (מטבע החיוב). משני: לאדם בדולר. בלי שינוי חישוב.
 */
export function buildMarketplacePackagePriceDisplay(
  ilsAmount,
  usdToIlsRate,
  passengerCount,
  roomInfo = null
) {
  const pax = Math.max(1, Math.floor(Number(passengerCount)) || 0);
  const resolved = resolvePublicPackageDisplayPrice({
    priceIlsRaw: ilsAmount,
    usdToIlsRate,
  });
  if (!resolved.displayedIls) {
    return {
      totalIlsLine: null,
      totalCaption: null,
      perPersonUsdLine: null,
      perPersonUnit: null,
      usdTotalHint: null,
    };
  }
  const couple = isCoupleRoomComposition(roomInfo, pax);
  const totalCaption =
    pax <= 1 ? "סה״כ לחבילה" : couple ? "סה״כ לזוג" : "סה״כ להרכב";
  return {
    totalIlsLine: resolved.displayedIls,
    totalCaption,
    perPersonUsdLine: formatUsdPerPersonFromIls(ilsAmount, usdToIlsRate, pax),
    perPersonUnit: "לאדם",
    usdTotalHint: resolved.displayedUsd,
  };
}

/** חיפוש / תהליך עד לפני תשלום — מחיר בדולרים בלבד */
export function displaySearchPriceUsd(ilsAmount, usdToIlsRate) {
  const formatted = formatUsdPrimaryIntegerFromIls(ilsAmount, usdToIlsRate);
  if (formatted == null) return null;
  return `החל מ־${formatted}`;
}

/**
 * אותו בסיס ILS כמו displaySearchPriceUsd, מחולק למספר נוסעים (מבוגרים+ילדים) — שורה אחת "לאדם".
 */
export function displaySearchPriceUsdPerPerson(ilsAmount, usdToIlsRate, passengerCount) {
  const formatted = formatUsdPerPersonFromIls(ilsAmount, usdToIlsRate, passengerCount);
  if (formatted == null) return null;
  return `החל מ־${formatted} לאדם`;
}

/** מחיר לאדם ללא ״החל מ־״ — כשמציגים אופציה ספציפית שנבחרה או חבילה יחידה ברורה */
export function displaySearchPriceUsdPerPersonExact(ilsAmount, usdToIlsRate, passengerCount) {
  const formatted = formatUsdPerPersonFromIls(ilsAmount, usdToIlsRate, passengerCount);
  if (formatted == null) return null;
  return `${formatted} לאדם`;
}

/**
 * חלקי שורת מחיר לכרטיס חבילה: קידומת "החל מ", סכום $, ו"לאדם" (לסטייל נפרד).
 * @param {{ includeStartingFrom?: boolean }} options — ברירת מחדל true
 */
export function getVacationPackageUsdPerPersonLineParts(
  ilsAmount,
  usdToIlsRate,
  passengerCount,
  options = {}
) {
  const { includeStartingFrom = true } = options;
  const amount = formatUsdPerPersonFromIls(ilsAmount, usdToIlsRate, passengerCount);
  if (amount == null) return null;
  return {
    prefix: includeStartingFrom ? "החל מ" : null,
    amount,
    perPerson: "לאדם",
  };
}

/** תשלום — סכום בשקלים (מחיר מקורי מהמערכת) */
export function displayPaymentPriceIls(ilsAmount) {
  const n = Number(ilsAmount);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return `${round2(n).toLocaleString("he-IL")}₪`;
}

/** תשלום — שורת סיכום: ש"ח + השוואת דולר לפי שער (הבהרה בלבד) */
export function displayPaymentPriceIlsWithUsdReference(ilsAmount, usdToIlsRate) {
  const ilsStr = displayPaymentPriceIls(ilsAmount);
  const usdFormatted = formatUsdPrimaryIntegerFromIls(ilsAmount, usdToIlsRate);
  if (usdFormatted == null) return { ilsLine: ilsStr, usdReferenceLine: null };
  return {
    ilsLine: ilsStr,
    usdReferenceLine: `לפי שער הדולר העדכני: ≈ ${usdFormatted}`,
  };
}

export function formatVacationPackageUsdAmount(usdAmount) {
  const n = Number(usdAmount);
  if (!Number.isFinite(n) || n <= 0) return null;
  return formatUSD(n);
}

/** סכום ב-ILS מהמערכת → מחרוזת $ בלבד (לשורות בסיכום ללא ״החל מ־״) */
export function formatIlsAmountAsUsdString(ilsAmount, usdToIlsRate) {
  return formatUsdPrimaryIntegerFromIls(ilsAmount, usdToIlsRate) ?? "—";
}

/** שווי ש"ח משני בסוגריים, לפי סכום השקלים המחושב במערכת */
export function formatNisSecondaryParenthetical(ilsAmount) {
  const n = Number(ilsAmount);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `(₪${Math.round(n).toLocaleString("en-US")})`;
}

/**
 * תצוגה אחידה לפני תשלום: דולר ראשי + ש"ח בסוגריים (כשהנתונים זמינים).
 * @returns {{ primaryUsd: string|null, secondaryIls: string|null, singleLine: string }}
 */
export function renderUsdWithIlsHint(ilsAmount, usdToIlsRate) {
  const primaryUsd = formatUsdPrimaryIntegerFromIls(ilsAmount, usdToIlsRate);
  const secondaryIls = formatNisSecondaryParenthetical(ilsAmount);
  let singleLine = "—";
  if (primaryUsd && secondaryIls) singleLine = `${primaryUsd} ${secondaryIls}`;
  else if (primaryUsd) singleLine = primaryUsd;
  else if (secondaryIls) singleLine = secondaryIls;
  return { primaryUsd, secondaryIls, singleLine };
}

/** מחרוזת שורה אחת: $1,604 (₪5,980) */
export function formatPrimaryUsdSecondaryIls(ilsAmount, usdToIlsRate) {
  return renderUsdWithIlsHint(ilsAmount, usdToIlsRate).singleLine;
}

/** טקסט חובה לשלב תשלום — חיוב בפועל בשקלים */
export const VACATION_PACKAGE_CHARGE_DISCLOSURE_HE =
  'החיוב יתבצע בש"ח לפי שער הדולר במועד החיוב';

/**
 * שלב תשלום: שני סכומים ברורים (ש"ח + דולר משוער) + אותו ניסוח חיוב.
 * @returns {{ ilsLine: string, usdLine: string|null, chargeDisclosure: string }}
 */
export function formatVacationPackagePaymentDualDisplay(ilsAmount, usdToIlsRate) {
  const n = Number(ilsAmount);
  const chargeDisclosure = VACATION_PACKAGE_CHARGE_DISCLOSURE_HE;
  if (!Number.isFinite(n) || n <= 0) {
    return { ilsLine: "—", usdLine: null, chargeDisclosure };
  }
  const ilsLine = `₪${Math.round(n).toLocaleString("he-IL")}`;
  const usdLine = formatUsdPrimaryIntegerFromIls(n, usdToIlsRate);
  return { ilsLine, usdLine, chargeDisclosure };
}

/**
 * סיכום חבילות לפני/בתוך תשלום — ש"ח בולט, דולר משוער קטן (הפוך מ־formatPrimaryUsdSecondaryIls).
 */
export function formatPrimaryIlsSecondaryUsd(ilsAmount, usdToIlsRate) {
  const n = Number(ilsAmount);
  if (!Number.isFinite(n) || n <= 0) {
    return { ilsLine: "—", usdLine: null };
  }
  const ilsLine = `₪${Math.round(n).toLocaleString("he-IL")}`;
  const usdLine = formatUsdPrimaryIntegerFromIls(n, usdToIlsRate);
  return { ilsLine, usdLine };
}

/** שורת סיכום הזמנה: ₪3,740 ($1,011) */
export function formatIlsWithUsdParenthetical(ilsAmount, usdToIlsRate) {
  const { ilsLine, usdLine } = formatPrimaryIlsSecondaryUsd(ilsAmount, usdToIlsRate);
  return usdLine && ilsLine !== "—" ? `${ilsLine} (${usdLine})` : ilsLine;
}

/**
 * תצוגת שער המחאות נוסעים — אותו מספר כמו בסרגל השערים (CurrencyRatesBar / formatFxRateDisplay).
 * מקור הנתון: AppContext.USDrate ← loadUSDRate() ← POST /getRate { currency: ILS, targetCurrency: USD } (currencyService.getCurrencyRate).
 */
export function formatTravelersChequeUsdRateLabel(usdToIlsRate) {
  const r = Number(usdToIlsRate);
  if (!Number.isFinite(r) || r <= 0) return null;
  const s = r.toFixed(4);
  return `₪${s} לדולר`;
}

function normalizeVpFxCurrency(code) {
  const c = String(code ?? "").trim().toUpperCase();
  if (!c) return null;
  if (c === "NIS") return "ILS";
  if (/^[A-Z]{3}$/.test(c)) return c;
  return null;
}

function positiveFxRate(value) {
  if (value == null || value === "") return null;
  if (typeof value === "object") return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function fareObjectFrom(value) {
  if (!value || typeof value !== "object") return null;
  if (value.ourTotalFare && typeof value.ourTotalFare === "object") {
    return value.ourTotalFare;
  }
  return value;
}

function readFxFromFare(value) {
  const fare = fareObjectFrom(value);
  if (!fare) return { currency: null, rate: null };
  return {
    currency: normalizeVpFxCurrency(
      fare.fx_currency || fare.originalCurrency || fare.fxCurrency
    ),
    rate:
      positiveFxRate(fare.fx_rate) ||
      positiveFxRate(fare.fxRate) ||
      positiveFxRate(fare.currencyRate),
  };
}

function dealRootFrom(selectedResult) {
  if (!selectedResult || typeof selectedResult !== "object") return null;
  return selectedResult.fullDeal && typeof selectedResult.fullDeal === "object"
    ? selectedResult.fullDeal
    : selectedResult;
}

/**
 * שער המטבע שממנו נגזר הסה״כ בש״ח במנוע LDS (fx_rate × מטבע דיל).
 * עדיפות לשער שנשמר על ה־fare בזמן החישוב; אחרת שער האדמין לאותו מטבע.
 */
export function resolveVacationPackageSourceFxToIls({
  ourPrice = null,
  selectedResult = null,
  roomsAndPaxInfo = null,
  currencyRate = null,
  usdToIlsRate = null,
} = {}) {
  const fareLikes = [];
  if (Array.isArray(roomsAndPaxInfo)) {
    for (const slot of roomsAndPaxInfo) {
      const tf = slot?.room?.ourFareDetails?.ourTotalFare;
      if (tf) fareLikes.push(tf);
    }
  }
  const deal = dealRootFrom(selectedResult);
  if (deal?.ourPrice) fareLikes.push(deal.ourPrice);
  if (deal?.ourTotalFare) fareLikes.push(deal.ourTotalFare);
  if (selectedResult?.ourPrice && selectedResult.ourPrice !== deal?.ourPrice) {
    fareLikes.push(selectedResult.ourPrice);
  }
  if (selectedResult?.ourTotalFare && selectedResult.ourTotalFare !== deal?.ourTotalFare) {
    fareLikes.push(selectedResult.ourTotalFare);
  }
  if (ourPrice) fareLikes.push(ourPrice);

  let currency = null;
  let rate = null;
  for (const fare of fareLikes) {
    const parsed = readFxFromFare(fare);
    if (!currency && parsed.currency) currency = parsed.currency;
    if (!rate && parsed.rate) rate = parsed.rate;
    if (currency && rate) break;
  }

  if (!currency) {
    currency = normalizeVpFxCurrency(
      deal?.fareDetails?.currency ||
        selectedResult?.fareDetails?.currency ||
        selectedResult?.fullDeal?.fareDetails?.currency
    );
  }

  if (currency === "ILS") return null;

  if (!rate && currency && currencyRate && typeof currencyRate === "object") {
    rate = positiveFxRate(currencyRate[currency]);
  }
  if (!rate && (!currency || currency === "USD")) {
    const usd =
      positiveFxRate(usdToIlsRate) ||
      positiveFxRate(currencyRate && typeof currencyRate === "object" ? currencyRate.USD : null);
    if (usd) return { currency: currency || "USD", rate: usd };
  }

  if (!currency || !rate) return null;
  return { currency, rate };
}

/** תצוגת שער המרה לסיכום הזמנה — אותו פורמט כמו סיכום מלון: 1 USD = ₪3.0566 */
export function formatVacationPackageSourceFxLabel(currency, rate) {
  const cur = normalizeVpFxCurrency(currency);
  const r = positiveFxRate(rate);
  if (!cur || cur === "ILS" || r == null) return null;
  return `1 ${cur} = ₪${r.toFixed(4)}`;
}

/** מספר שלב (1-based) של תשלום בזרימת inline — לפי האם יש שלב כבודה בתשלום */
export function vacationPackageInlinePaymentStepNumber(needsBaggagePurchaseStep) {
  return needsBaggagePurchaseStep ? 5 : 4;
}

/** אינדקס שלב (0-based) של תשלום בזרימה הסטנדרטית (אחרי פרטי חבילה, חדרים, נוסעים, תוספות) */
export function vacationPackageStandardPaymentStepIndex() {
  return 4;
}
